import React, {
  MouseEventHandler,
  ReactElement,
  useCallback,
  useState,
} from 'react';
import { Auth } from '@aws-amplify/auth';
import { setItem } from 'helpers/local-storage';
import useNav from 'hooks/nav';

import Login from './login';
import Verification from './verification';
import * as Sentry from '@sentry/browser';
import './identification.scss';
import { customersApi } from 'api/bestpark/customers';
import { getValidPhoneNumber } from 'helpers/phone-number-utils';

interface IdentificationProps {
  back: MouseEventHandler;
}

const screens = ['login', 'verification'];

export default function Identification({
  back,
}: IdentificationProps): ReactElement {
  const { goTo, locale } = useNav();
  const [currentScreen, setCurrentScreen] = useState(screens[0]);
  const [existingCustomer, setExistingCustomer] = useState<Customer | null>(
    null,
  );
  const [cognitoUser, setCognitoUser] = useState(null);

  const onBackButtonClick = (event: any): void => {
    if (currentScreen === screens[1]) {
      setCurrentScreen(screens[0]);
    } else {
      back(event);
    }
  };

  const onNextButtonClick = (path?: string): void => {
    if (currentScreen === screens[0]) {
      setCurrentScreen(screens[1]);
    } else {
      goTo(`${path}`);
    }
  };

  const submitLogin = useCallback(async (phone: string): Promise<void> => {
    const cognitoUser = await Auth.signIn(phone);
    setItem(
      'cognitoUser',
      JSON.stringify({ username: phone, session: cognitoUser.Session }),
    );
    setCognitoUser(cognitoUser);
  }, []);

  const sendVerificationCode = useCallback(
    async (phoneNumber: string, reCaptchaToken: string) => {
      const customer = await customersApi.updateLoginIntent({
        phoneNumber,
        reCaptchaToken,
        locale,
      });
      // In case of existing customer, get phone number for logging in from the customer object
      // returned by the back-end to make sure it matches with the one in cognito consumer user pool.
      // For new users, remove extra zeros from the phone number.
      const loginPhoneNumber = customer
        ? customer.phoneNumber
        : getValidPhoneNumber(phoneNumber);
      if (loginPhoneNumber) {
        await submitLogin(loginPhoneNumber);
      } else {
        Sentry.withScope((scope) => {
          scope.setLevel(Sentry.Severity.Error);
          scope.setExtra('phoneNumber', phoneNumber);
          Sentry.captureMessage(
            'User is trying to log in with an invalid phone number',
          );
        });
      }
      setExistingCustomer(customer);
      return loginPhoneNumber;
    },
    [locale, submitLogin],
  );

  return currentScreen === screens[0] ? (
    <Login next={onNextButtonClick} sendCode={sendVerificationCode} />
  ) : (
    <Verification
      back={onBackButtonClick}
      existingCustomer={existingCustomer}
      next={onNextButtonClick}
      sendCode={sendVerificationCode}
      user={cognitoUser}
    />
  );
}
